import { useSignUpPasswordless } from "Api/Mutations/Auth/useSignUpPasswordless";
import { useUpdateTransactionTip } from "Api/Mutations/Transactions/useUpdateTransactionTip";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import type { PaymentStep } from "Components/AnonymousCustomer/Payment/config";
import { getPayButtonLabel } from "Components/Customer/Payment/PaymentForms/payButtonUtils";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { UserStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

type Props = {
  onNextStepRequested: (nextStep?: PaymentStep) => void;
  transactionPublicID: string;
  campaignCode?: string;
  affiliateCode?: string;
  pendingTipAmount?: number;
};

export const InitiatePaymentButton = ({
  onNextStepRequested,
  transactionPublicID,
  campaignCode,
  affiliateCode,
  pendingTipAmount,
}: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const { data: transaction } = useTransactionsDetail({
    publicID: transactionPublicID,
  });
  const {
    data: bankingAccounts,
    isLoading: isLoadingBankingAccounts,
    refetch,
  } = useBankingAccounts(undefined, !!user);
  const { mutate: signUpPasswordLess, isPending } = useSignUpPasswordless({
    onSignUpSuccess: () => onNextStepRequested("bank"),
    onError: () => toast.error(t(Resources.TransactionErrors.UnknownError)),
    transactionPublicID,
    campaignCode,
    affiliateCode,
  });
  const { mutate: updateTip, isPending: isUpdatingTip } =
    useUpdateTransactionTip();

  const proceedToPayment = async () => {
    if (user?.userStatus !== UserStatus.Anonymous) {
      signUpPasswordLess(undefined);
    } else {
      // If banking accounts are still loading, wait for them to complete
      let accountsData = bankingAccounts;
      if (isLoadingBankingAccounts) {
        const result = await refetch();
        accountsData = result.data;
      }
      onNextStepRequested(accountsData?.items.length ? "account" : "bank");
    }
  };

  const onPay = () => {
    const isTipChanged =
      pendingTipAmount != null && pendingTipAmount !== transaction?.tipAmount;

    if (!isTipChanged) {
      return proceedToPayment();
    }

    updateTip(
      { publicID: transactionPublicID, tipAmount: pendingTipAmount },
      { onSuccess: proceedToPayment },
    );
  };

  const totalAmount =
    pendingTipAmount != null && transaction
      ? transaction.amount - transaction.tipAmount + pendingTipAmount
      : transaction?.amount;

  return (
    <PrimaryButton
      fullWidth
      color="primary"
      onClick={onPay}
      isLoading={isPending || isUpdatingTip}
    >
      {getPayButtonLabel(t, totalAmount, transaction?.currencyCode)}
    </PrimaryButton>
  );
};
